import { useCallback } from 'preact/hooks';

type PressOptions = {
  isDisabled?: boolean;
};

const DefaultSettings = {
  isDisabled: false
};

// JET-77334: Prevent browser default behavior where onClick is fired continuously if you
// hold down the Enter key. We also disable repeats for Space to prevent cases where you
// hold the key down, transfer focus to the element, and then release the key. We only want
// to invoke the press handler when you press and release on the same element.
const onKeyDown = (event: KeyboardEvent) => {
  if ((event.code === 'Enter' || event.code === 'Space') && event.repeat) {
    event.preventDefault();
  }
};

/**
 * Returns a click handler that can make a target element either clickable or keyboard pressable.
 * Note that handling repeated keys (holding down Enter or Space) is disabled.
 *
 * @param onPressHandler function
 * @param isDisabled boolean
 * @returns
 */

export function usePressClick(
  onPressHandler: (event: Event) => void,
  settings: PressOptions = DefaultSettings
): { pressProps: Record<string, any> } {
  const onClick = useCallback(
    (event: Event) => {
      // See note above about why propagation is stopped: to prevent nested component bubbling clicks issue
      event.stopPropagation();
      // in legacy, we would check event.detail <= 1 to fire only once on multi-clicks,
      // however, for now keeping this simple.
      onPressHandler(event);
    },
    [onPressHandler]
  );

  const pressProps = settings?.isDisabled ? {} : { onClick, onKeyDown };

  return {
    pressProps
  };
}
